// components/SeccionEventoModal.js
import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaTicketAlt } from 'react-icons/fa';

import { getAllSeccionesByEventoForAdmin,     
    createSeccionEvento, 
    updateSeccionEvento, 
    deleteSeccionEvento, 
    toggleSeccionEventoStatus  } from '../../../../../components/http/Actions/SeccionEventoAction';

const SeccionEventoModal = ({ evento, onClose, showNotification }) => {
    const [secciones, setSecciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingSeccion, setEditingSeccion] = useState(null);
    const [formData, setFormData] = useState({
        nombreSeccion: '',
        descripcion: '',
        precio: '',
        entradasDisponibles: ''
    });

    const token = localStorage.getItem("token");

    // Cargar secciones al abrir el modal
    useEffect(() => {
        fetchSecciones();
    }, []);

    const fetchSecciones = async () => {
        setLoading(true);
        try {
            await getAllSeccionesByEventoForAdmin(
                evento.eventoId,
                setSecciones,
                setError,
                token
            );
        } catch (error) {
            console.error("Error loading secciones:", error);
        } finally {
            setLoading(false);
        }
    };

    // Formatear precio para mostrar (chileno)
    const formatPrice = (price) => {
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(price);
    };

    // Limpiar precio para enviar al backend
    const cleanPrice = (priceText) => {
        return priceText.replace(/[^\d]/g, '');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'precio') {
            // Solo permitir números
            const numericValue = value.replace(/[^\d]/g, '');
            setFormData(prev => ({
                ...prev,
                [name]: numericValue ? new Intl.NumberFormat('es-CL').format(parseInt(numericValue)) : ''
            }));
        } else if (name === 'entradasDisponibles') {
            // Solo números enteros
            const numericValue = value.replace(/[^\d]/g, '');
            setFormData(prev => ({
                ...prev,
                [name]: numericValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const dataToSend = {
            eventoId: evento.eventoId,
            nombreSeccion: formData.nombreSeccion,
            descripcion: formData.descripcion,
            precio: parseFloat(cleanPrice(formData.precio)),
            entradasDisponibles: parseInt(formData.entradasDisponibles)
        };

        try {
            if (editingSeccion) {
                // Actualizar
                await updateSeccionEvento(
                    editingSeccion.seccionEventoId,
                    {
                        ...dataToSend,
                        habilitado: editingSeccion.habilitado
                    },
                    setError,
                    token
                );
                showNotification('Sección actualizada exitosamente', 'success');
            } else {
                // Crear
                await createSeccionEvento(dataToSend, setError, token);
                showNotification('Sección creada exitosamente', 'success');
            }

            // Resetear formulario
            setFormData({
                nombreSeccion: '',
                descripcion: '',
                precio: '',
                entradasDisponibles: ''
            });
            setShowCreateForm(false);
            setEditingSeccion(null);
            
            // Recargar secciones
            await fetchSecciones();
        } catch (error) {
            console.error('Error saving seccion:', error);
            showNotification('Error al guardar la sección', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (seccion) => {
        setEditingSeccion(seccion);
        setFormData({
            nombreSeccion: seccion.nombreSeccion,
            descripcion: seccion.descripcion || '',
            precio: new Intl.NumberFormat('es-CL').format(seccion.precio),
            entradasDisponibles: seccion.entradasDisponibles.toString()
        });
        setShowCreateForm(true);
    };

    const handleDelete = async (seccionId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar esta sección?')) {
            try {
                await deleteSeccionEvento(seccionId, setError, token);
                showNotification('Sección eliminada exitosamente', 'success');
                await fetchSecciones();
            } catch (error) {
                showNotification('Error al eliminar la sección', 'error');
            }
        }
    };

    const handleToggleStatus = async (seccionId) => {
        try {
            await toggleSeccionEventoStatus(seccionId, setError, token);
            showNotification('Estado de la sección actualizado', 'success');
            await fetchSecciones();
        } catch (error) {
            showNotification('Error al cambiar el estado', 'error');
        }
    };

    const cancelForm = () => {
        setShowCreateForm(false);
        setEditingSeccion(null);
        setFormData({
            nombreSeccion: '',
            descripcion: '',
            precio: '',
            entradasDisponibles: ''
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 overflow-y-auto backdrop-blur-sm">
            <div className="bg-zinc-800 rounded-xl shadow-2xl max-w-6xl w-full p-8 my-8 max-h-[90vh] overflow-y-auto border border-zinc-700">
                {/* Header */}
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="text-2xl font-bold text-white mb-2">
                            Gestionar Secciones
                        </h3>
                        <div className="text-gray-300">
                            <p className="font-medium">{evento.titulo}</p>
                            <p className="text-sm text-gray-400">
                                {new Date(evento.fecha).toLocaleDateString('es-CL')} - {evento.lugar?.nombre}
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-white hover:bg-zinc-700 p-2 rounded-full transition-all duration-200"
                    >
                        <FaTimes />
                    </button>
                </div>

                {error && (
                    <div className="bg-red-500/20 border border-red-500 text-red-100 px-4 py-3 rounded-lg mb-6">
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                {/* Botón para crear nueva sección */}
                {!showCreateForm && (
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center mb-6"
                    >
                        <FaPlus className="mr-2" />
                        Nueva Sección
                    </button>
                )}

                {/* Formulario de crear/editar */}
                {showCreateForm && (
                    <div className="bg-zinc-700/50 rounded-lg p-6 mb-6 border border-zinc-600">
                        <h4 className="text-lg font-semibold text-white mb-4">
                            {editingSeccion ? 'Editar Sección' : 'Nueva Sección'}
                        </h4>
                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Nombre de la Sección *
                                    </label>
                                    <input
                                        type="text"
                                        name="nombreSeccion"
                                        value={formData.nombreSeccion}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        placeholder="Ej: VIP, General, Platea..."
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Precio *
                                    </label>
                                    <input
                                        type="text"
                                        name="precio"
                                        value={formData.precio}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        placeholder="15.000"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Entradas Disponibles *
                                    </label>
                                    <input
                                        type="text"
                                        name="entradasDisponibles"
                                        value={formData.entradasDisponibles}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        placeholder="100"
                                        required
                                    />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Descripción
                                    </label>
                                    <input
                                        type="text"
                                        name="descripcion"
                                        value={formData.descripcion}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                                        placeholder="Descripción opcional..."
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={cancelForm}
                                    className="px-4 py-2 bg-zinc-600 text-white rounded-lg hover:bg-zinc-500 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                                >
                                    {loading ? 'Guardando...' : editingSeccion ? 'Actualizar' : 'Crear'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Lista de secciones */}
                <div>
                    <h4 className="text-lg font-semibold text-white mb-4">
                        Secciones del Evento ({secciones.length})
                    </h4>
                    
                    {loading && secciones.length === 0 ? (
                        <div className="flex justify-center py-8">
                            <div className="w-8 h-8 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
                        </div>
                    ) : secciones.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                            <FaTicketAlt className="mx-auto text-4xl mb-4 opacity-50" />
                            <p>No hay secciones creadas para este evento</p>
                            <p className="text-sm">Crea la primera sección para comenzar a vender entradas</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {secciones.map((seccion) => (
                                <div 
                                    key={seccion.seccionEventoId} 
                                    className={`bg-zinc-700/50 rounded-lg p-4 border transition-all duration-200 ${
                                        seccion.habilitado ? 'border-zinc-600' : 'border-zinc-600 opacity-60'
                                    }`}
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <h5 className="font-semibold text-white">{seccion.nombreSeccion}</h5>
                                        <div className="flex space-x-1">
                                            <button
                                                onClick={() => handleToggleStatus(seccion.seccionEventoId)}
                                                className={`p-1 rounded transition-colors ${
                                                    seccion.habilitado 
                                                        ? 'text-green-400 hover:text-green-300' 
                                                        : 'text-gray-400 hover:text-gray-300'
                                                }`}
                                                title={seccion.habilitado ? 'Deshabilitar' : 'Habilitar'}
                                            >
                                                {seccion.habilitado ? <FaToggleOn /> : <FaToggleOff />}
                                            </button>
                                            <button
                                                onClick={() => handleEdit(seccion)}
                                                className="p-1 text-blue-400 hover:text-blue-300 transition-colors"
                                                title="Editar"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(seccion.seccionEventoId)}
                                                className="p-1 text-red-400 hover:text-red-300 transition-colors"
                                                title="Eliminar"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Precio:</span>
                                            <span className="text-white font-medium">{formatPrice(seccion.precio)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Disponibles:</span>
                                            <span className="text-white">{seccion.entradasDisponibles}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Vendidas:</span>
                                            <span className="text-white">{seccion.entradasVendidas}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-400">Restantes:</span>
                                            <span className={`font-medium ${
                                                seccion.entradasRestantes > 0 ? 'text-green-400' : 'text-red-400'
                                            }`}>
                                                {seccion.entradasRestantes}
                                            </span>
                                        </div>
                                        
                                        {seccion.descripcion && (
                                            <div className="pt-2 border-t border-zinc-600">
                                                <p className="text-gray-300 text-xs">{seccion.descripcion}</p>
                                            </div>
                                        )}
                                        
                                        {seccion.agotadoEntradas && (
                                            <div className="bg-red-900/30 border border-red-600/50 rounded px-2 py-1 text-center">
                                                <span className="text-red-300 text-xs font-medium">AGOTADO</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SeccionEventoModal;